import React, { useEffect, useState } from "react";
import { Button, Drawer, Form, Input, Select, Space, Typography, message } from "antd";
import { CircleCheck, Info } from "lucide-react";
import axios from "axios";

import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_PAM_API } from "../../constants";
// import { DEVICE_TYPES, clientsConfSnippet, deviceDisplayName, deviceTypeLabel, generateSharedSecret, isValidDeviceIp } from "./radiusDeviceModel";
import {
  DEVICE_TYPES,
  RADIUS_CLIENT_KINDS,
  clientsConfSnippet,
  deviceDisplayName,
  deviceTypeText,
  generateSharedSecret,
  isValidDeviceIp,
} from "./radiusDeviceModel";
import { radiusErrorText, serverIsActive, updateRadiusDevice } from "../../service/radiusService";
import { AN_MONO } from "../../design-system/tokens";
import { CodeBlock, Note } from "../../pages/enduser/ssh-proxy/sshProxyUi";

const { Text, Paragraph } = Typography;

const headers = () => ({ withCredentials: true, "X-Authorization": AUTH_TOKEN() });

// updateRadiusDevice is callback-style; this lets handleFinish await it.
const updateDevice = (body) =>
  new Promise((resolve, reject) => updateRadiusDevice({ ...body, onSuccess: resolve, onFailure: reject }));

// Add or edit one network device.
//
// Adding saves through PAM's existing CreateDevice, then shows the clients.conf block the
// admin pastes into FreeRADIUS. The shared secret is generated here and shown once: it is
// only needed by FreeRADIUS and the device, so AuthNull never stores it. Adding is optional:
// devices register themselves on their first login; adding one first names it for a policy.
//
// Editing uses the RADIUS-owned UpdateDevice (PAM's API has no update): name, type,
// NAS-Identifier and the RADIUS server it sends logins to. The address is read-only there,
// because it is the key PAM's delete and duplicate check use. Saving clears "Discovered".
//
// servers: ListServers rows, for "Sends logins to". activity: this device's DeviceActivity row.
export default function RadiusDeviceDrawer({ open, device, activity, servers = [], onClose, onSaved }) {
  const isEdit = !!device;
  const [form] = Form.useForm();
  const [messageApi, messageContext] = message.useMessage();
  const [saving, setSaving] = useState(false);
  const [snippet, setSnippet] = useState("");
  const [clientKind, setClientKind] = useState("unknown");

  useEffect(() => {
    if (!open) return;
    setSnippet("");
    form.resetFields();
    if (device) {
      const type = deviceTypeText(device);
      form.setFieldsValue({
        name: deviceDisplayName(device),
        ip: device.ip_address || device.client_ip || "",
        nasIdentifier: device.nas_identifier || "",
        deviceType: type === "Not set" ? undefined : type,
        radiusServerId: activity?.radiusServerId || undefined,
      });
    } else if (servers.length === 1) {
      // One server: it is the one the device sends logins to.
      form.setFieldsValue({ radiusServerId: servers[0].id });
    }
    // Only when the panel opens: the page re-reads servers and activity after a save, which must
    // not wipe the clients.conf block shown after adding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device, form]);

  // OLD: const errorText = (err, fallback) => err?.response?.data?.error || err?.response?.data?.message || fallback;
  const errorText = (err, fallback) => radiusErrorText(err, fallback);

  const handleFinish = async (values) => {
    const name = values.name.trim();
    const ip = values.ip.trim();
    const nasIdentifier = (values.nasIdentifier || "").trim();
    const deviceType = values.deviceType || "";
    // "" clears it; UpdateDevice leaves it alone only when the key is absent.
    const radiusServerId = values.radiusServerId || "";
    setSaving(true);
    try {
      if (isEdit) {
        // OLD: axios.post(`${REACT_APP_PAM_API}/radius/UpdateDevice`, { id: device.id, name, deviceType, nasIdentifier }, ...)
        await updateDevice({ id: device.id, name, deviceType, nasIdentifier, radiusServerId });
        messageApi.success("Device updated");
        onSaved && onSaved();
        onClose && onClose();
        return;
      }
      if (!ORG_ID()) {
        messageApi.error("Your session has no organisation. Sign in again.");
        return;
      }
      // Field names exactly as PAM's CreateDevice binds them. The address goes in both
      // ip_address (its meaning) and client_ip (PAM's key for duplicates and delete).
      const res = await axios.post(
        `${REACT_APP_PAM_API}/network_device/CreateDevice`,
        {
          name,
          device_type: deviceType,
          device_id: "",
          ip_address: ip,
          client_ip: ip,
          nas_identifier: nasIdentifier,
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
          nasPort: 0,
          nas_port_type: "",
          nas_port_id: "",
          eventType: "radius_manual",
        },
        { headers: headers() },
      );
      // CreateDevice has no server field: set it on the new row through UpdateDevice.
      const newId = res?.data?.id;
      if (radiusServerId && newId) {
        try {
          await updateDevice({ id: newId, name, deviceType, nasIdentifier, radiusServerId });
        } catch (err) {
          messageApi.warning("Device added, but its RADIUS server was not saved. Edit the device to set it.");
        }
      }
      // OLD: setSnippet(clientsConfSnippet({ name, ip, secret: generateSharedSecret() }));
      const kind = values.radiusClient || "unknown";
      setClientKind(kind);
      setSnippet(clientsConfSnippet({ name, ip, secret: generateSharedSecret(), kind }));
      onSaved && onSaved();
    } catch (err) {
      messageApi.error(errorText(err, isEdit ? "Could not update the device" : "Could not add the device"));
    } finally {
      setSaving(false);
    }
  };

  const serverOptions = servers.map((s) => ({
    value: s.id,
    label: serverIsActive(s) ? s.name : `${s.name} (Inactive)`,
  }));
  // A device pointing at a server the list no longer has (deleted elsewhere) still shows its id.
  if (activity?.radiusServerId && !servers.some((s) => s.id === activity.radiusServerId)) {
    serverOptions.push({ value: activity.radiusServerId, label: "Unknown server" });
  }

  return (
    <Drawer
      // OLD: title={isEdit ? "Edit Radius Device" : "Add Radius Device"}
      title={isEdit ? "Edit device" : "Add device"}
      open={open}
      onClose={onClose}
      // OLD: width={520}
      width="min(32.5rem, 100vw)"
      destroyOnClose
      extra={
        snippet ? (
          <Button type="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              Save
            </Button>
          </Space>
        )
      }
    >
      {messageContext}
      {!snippet && (
        <Form form={form} layout="vertical" onFinish={handleFinish} requiredMark>
          {isEdit && activity?.status === "discovered" && (
            <div style={{ marginBottom: "1rem" }}>
              <Note tone="info" icon={<Info size={15} />}>
                This device registered itself on its first login. Saving it with a name marks it as named, so it is no
                longer Discovered.
              </Note>
            </div>
          )}
          {!isEdit && (
            <div style={{ marginBottom: "1rem" }}>
              <Note tone="info" icon={<Info size={15} />}>
                Optional: devices appear on their own after their first login. Add one now to name it before then, so a
                policy can pick it.
              </Note>
            </div>
          )}
          <Form.Item label="Name" name="name" rules={[{ required: true, whitespace: true, message: "Enter a name" }]}>
            <Input placeholder="Office VPN" />
          </Form.Item>
          <Form.Item
            label="NAS IP"
            name="ip"
            extra={isEdit ? "The address can't be changed. To move a device, delete it and add it again." : "The address your device sends RADIUS requests from."}
            rules={[
              { required: true, message: "Enter the device's IP address" },
              { validator: (_, v) => (!v || isValidDeviceIp(v) ? Promise.resolve() : Promise.reject(new Error("Not a valid IP address"))) },
            ]}
          >
            <Input placeholder="10.0.0.5" disabled={isEdit} style={{ fontFamily: AN_MONO }} />
          </Form.Item>
          <Form.Item label="Type" name="deviceType">
            <Select allowClear placeholder="Not set" options={DEVICE_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item label="NAS-Identifier" name="nasIdentifier" extra="Optional. The name the device reports in RADIUS (NAS-Identifier), e.g. vpn-hq.">
            <Input placeholder="vpn-hq" />
          </Form.Item>
          <Form.Item
            label="Sends logins to"
            name="radiusServerId"
            extra={servers.length ? "The RADIUS server this device's clients.conf entry is on." : "No RADIUS servers yet. Add one on RADIUS Servers."}
          >
            <Select allowClear placeholder="Not set" options={serverOptions} notFoundContent="No RADIUS servers" />
          </Form.Item>
          {!isEdit && (
            <Form.Item
              label="RADIUS client"
              name="radiusClient"
              initialValue="unknown"
              extra="Sets the BlastRADIUS lines of the clients.conf block shown after saving."
            >
              <Select options={RADIUS_CLIENT_KINDS} />
            </Form.Item>
          )}
          {isEdit && (
            <Text type="secondary">
              The shared secret is only shown when a device is added. To change it, edit clients.conf on your FreeRADIUS server.
            </Text>
          )}
        </Form>
      )}

      {snippet && (
        <>
          {/* OLD: <Alert type="success" showIcon message="Device added" style={{ marginBottom: 16 }} /> */}
          <div style={{ marginBottom: "1rem" }}>
            <Note tone="ok" icon={<CircleCheck size={15} />}>
              Device added
            </Note>
          </div>
          <Paragraph>
            Add this to <Text code>/etc/freeradius/3.0/clients.conf</Text> on your FreeRADIUS server, then restart FreeRADIUS:
          </Paragraph>
          {/* OLD: a light-grey <pre> (#f5f5f5) with a copy icon; CodeBlock reads in dark mode too. */}
          <CodeBlock copyText={snippet} multiline>
            {snippet}
          </CodeBlock>
          <Paragraph type="secondary" style={{ marginTop: "0.75rem" }}>
            The secret is shown only once. Copy it now.
          </Paragraph>
          {/* <Paragraph type="secondary">
            If the device sends Message-Authenticator (strongSwan does; OpenVPN&apos;s RADIUS plugin does not), also add{" "}
            <Text code>require_message_authenticator = yes</Text> to the block.
          </Paragraph> */}
          {clientKind === "unknown" && (
            <Paragraph type="secondary">
              If the device sends Message-Authenticator (strongSwan does, and so does most firmware with the 2024
              BlastRADIUS fixes; OpenVPN&apos;s RADIUS plugin does not), replace the last two lines with{" "}
              <Text code>require_message_authenticator = yes</Text>.
            </Paragraph>
          )}
          {clientKind === "sends-ma" && (
            <Paragraph type="secondary">
              If logins from this device get no answer after adding the block, its firmware does not send
              Message-Authenticator: change the line to <Text code>require_message_authenticator = no</Text> and add{" "}
              <Text code>limit_proxy_state = yes</Text>.
            </Paragraph>
          )}
          <Paragraph>On the device, set the RADIUS timeout to at least 60 seconds with 1 retry.</Paragraph>
        </>
      )}
    </Drawer>
  );
}
